import React from "react";
import PropTypes from "prop-types";

import "./advanced-query-builder.scss";

const Steve = require("@gluenetworks/steve_core").getInstance();

const AdvancedFilter = require("@gluenetworks/entity_grid_gl").AdvancedFilter;

const _ = require("lodash");

const PlatformSdk = require("@gluenetworks/platform-sdk-remote");
const SiteEntity = PlatformSdk.entities.ws.Site;
const TagsEntity = PlatformSdk.entities.ws.Tags;

const customFieldsList = [];

class AdvancedQueryBuilder extends React.PureComponent {
    constructor(props) {
        super(props);
        this.queryContainer = React.createRef();
        this.queryBuilder = undefined;
        this.optionMapSitePaths = [];
        this.optionMapTags = [];
        this.advancedFilterContainer = document.createElement("advanced-filter-container");
        this.state = {
            validFilter: true,
            filterReset: false,
        };
    }

    async componentDidMount() {
        // Neither option load should block the mount: on failure only its own field is
        // degraded, to an empty select.
        await Promise.all([
            this._getSitePaths().catch((err) =>
                console.warn("Failed to load site path filter options", err)
            ),
            this._getTags().catch((err) => console.warn("Failed to load tag filter options", err)),
        ]);
        this.handleCreateQueryBuilder(this.advancedFilterContainer);
        this.addIconsHandler();
    }

    componentWillUnmount() {
        if (this.queryBuilder && typeof this.queryBuilder.removeAllListeners === "function") {
            this.queryBuilder.removeAllListeners("PlanStatus");
            this.queryBuilder.renderer.applyBtn.removeAllListeners("UserClick");
        }
    }

    handleCreateQueryBuilder = (filterContainer) => {
        const objForQueryBuilder = {
            filterData: this.props.filterData,
            columnsConfig: this.getFilterOptions(this.props.columns, customFieldsList),
        };

        if (
            !_.isEmpty(this.props.type) &&
            (this.props.type.value.indexOf("/DEVICES") > -1 || this.props.type.value === "DEVICES")
        ) {
            // Add Site Path List filter if not already present
            const sitePathFilter = {
                name: "siteId",
                label: "Site Path List",
                dataType: "pathlist",
                options: this.optionMapSitePaths,
            };

            if (!objForQueryBuilder.columnsConfig.some((c) => c.name === "siteId")) {
                objForQueryBuilder.columnsConfig.push(sitePathFilter);
            }

            const tagIdsFilter = {
                name: "tagIds",
                label: "Tags",
                dataType: "array",
                options: this.optionMapTags,
                forceSelect: true,
            };

            if (!objForQueryBuilder.columnsConfig.some((c) => c.name === "tagIds")) {
                objForQueryBuilder.columnsConfig.push(tagIdsFilter);
            }

            const tagNamesFilter = {
                name: "tagNames",
                label: "Tag Name",
                dataType: "array",
            };

            if (!objForQueryBuilder.columnsConfig.some((c) => c.name === "tagNames")) {
                objForQueryBuilder.columnsConfig.push(tagNamesFilter);
            }
        }

        this.queryBuilder = new AdvancedFilter(objForQueryBuilder);

        this.queryBuilder.on("AdvancedFilterRuleChanged", this.addIconsHandler);
        this.queryBuilder.renderer.applyBtn.on("UserClick", this.applyFilter);
        this.queryBuilder.renderer.resetBtn.on("UserClick", this.resetFilter);

        filterContainer.appendChild(this.queryBuilder.getRootElement());
        this.queryContainer.current.appendChild(filterContainer);
    };

    addIconsHandler = () => {
        const icons = [...document.getElementsByClassName("glyphicon-remove")];
        icons.forEach((element) => {
            element.classList.add("g-icon");
            element.classList.add("g-icon-delete");
        });
    };

    applyFilter = () => {
        const builderContainer = this.queryBuilder.renderer.$builderContainer;
        const transformedMongoQuery = builderContainer.queryBuilder("getMongo");
        if (transformedMongoQuery || this.state.filterReset) {
            this.setState({
                ...this.state,
                validFilter: true,
            });
            this.props.saveFilter({
                mongoQuery: transformedMongoQuery,
                humanQuery: transformedMongoQuery,
            });
            this.props.closeConfigModal();
        } else {
            this.setState({
                ...this.state,
                validFilter: false,
            });
        }
    };

    resetFilter = () => {
        this.setState({
            ...this.state,
            filterReset: true,
        });
    };

    getFilterOptions = (columnsData, customFields) => {
        Object.values(columnsData).forEach((el) => {
            if (el.path && el.path.indexOf("customFieldValues.") > -1) {
                const path = el.path.split("customFieldValues.")[1];
                if (_.isEmpty(columnsData[`customFieldValues.${path}`])) {
                    columnsData[`customFieldValues.${path}`] = columnsData[path];
                }
                delete columnsData[path];
            }

            if (el.filterLabel) {
                el.label = el.filterLabel;
            }
        });

        const formattedColumnsData = Object.keys(columnsData).map((el) => {
            const obj = columnsData[el];
            if (!_.isUndefined(columnsData[el].name)) {
                obj.id = columnsData[el].name;
                obj.field = columnsData[el].dataType;
                obj.name = columnsData[el].name;
            } else {
                obj.id = el;
                obj.field = el;
                obj.name = el;
            }
            return obj;
        });

        // Deduplicate formattedColumnsData by id (customFields is guaranteed unique and non-overlapping)
        const seen = new Set();
        const filterOptions = [
            ...formattedColumnsData.filter(({id}) => !seen.has(id) && seen.add(id)),
            ...customFields,
        ];
        const allowedCustomFieldTypes = ["boolean", "string", "enum"];

        return filterOptions
            .filter((option) => {
                if (option.type && !allowedCustomFieldTypes.includes(option.type.toLowerCase())) {
                    return false;
                }
                return true;
            })
            .map((optData) => ({
                ...optData,
                dataType:
                    optData.dataType || (optData.type ? optData.type.toLowerCase() : undefined),
            }));
    };

    _hasAccessToCDA() {
        const hasPermission = !!_.get(Steve, "userSession._userEntity.authorizedActions").filter(
                (action) => action === "configDriftAndAuditAccess"
            ),
            hasLicense = Steve.licenseRestriction.checkLicense("CDA");

        if (hasPermission && hasLicense) {
            return true;
        }
        return false;
    }

    render() {
        return (
            <>
                {!this.state.validFilter && (
                    <div className="glu-advanced-filter">Filter rules are incorrectly set.</div>
                )}
                <div className="glu-query" ref={this.queryContainer} />
            </>
        );
    }

    async _getSitePaths() {
        const tid = _.get(Steve, "userSession.activeOrganization.id"),
            sitePaths = await SiteEntity.loadSites(
                tid,
                {},
                {
                    id: true,
                    path: true,
                },
                {
                    path: 1,
                }
            );

        this.optionMapSitePaths = sitePaths
            .map(({id, path}) => ({
                value: id,
                label: path,
            }))
            .filter((item) => item.label !== undefined);
    }

    async _getTags() {
        const tid = _.get(Steve, "userSession.activeOrganization.id"),
            tags = await TagsEntity.loadTags(
                tid,
                {},
                {
                    id: true,
                    name: true,
                },
                {
                    name: 1,
                }
            );

        // The DB value is the tag id, the display is its name.
        this.optionMapTags = tags
            .map(({id, name}) => ({value: id, label: name}))
            .filter((item) => item.label !== undefined);
    }
}

AdvancedQueryBuilder.propTypes = {
    saveFilter: PropTypes.func.isRequired,
    closeConfigModal: PropTypes.func.isRequired,
    filterData: PropTypes.objectOf(PropTypes.any).isRequired,
    // eslint-disable-next-line react/require-default-props
    columns:
        PropTypes.arrayOf(PropTypes.any).isRequired || PropTypes.objectOf(PropTypes.any).isRequired,
};

export default AdvancedQueryBuilder;
